// @ts-nocheck
import { BrowserMultiFormatReader } from "@zxing/library";
import React, { useCallback, useEffect, useRef, useState } from "react";
import Webcam from "react-webcam";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import "./TicketBarcodeScannerButton.scss";

const TICKET_SCANNER_MODAL_Z_INDEX = 2070;
const SCAN_TICK_MS = 80;
const CONFIRM_HIT_COUNT = 2;
const MAX_DECODE_WIDTH = 720;

const VIDEO_CONSTRAINTS = {
  width: { ideal: 1280 },
  height: { ideal: 720 },
  facingMode: { ideal: "environment" },
};

const normalizeScannedText = text =>
  String(text ?? "")
    .trim()
    .replace(/[\u0000-\u001F\u007F]/g, "");

const createCodeReader = () => new BrowserMultiFormatReader();

const pickLeadingCandidate = candidateHits => {
  let bestCode = "";
  let bestCount = 0;
  let secondCount = 0;

  candidateHits.forEach((count, code) => {
    if (count > bestCount) {
      secondCount = bestCount;
      bestCount = count;
      bestCode = code;
    } else if (count > secondCount) {
      secondCount = count;
    }
  });

  const isConfirmed =
    bestCount >= CONFIRM_HIT_COUNT && bestCount > secondCount;

  return isConfirmed ? bestCode : "";
};

const TicketBarcodeScannerButton = ({ handleResult }) => {
  const webcamRef = useRef(null);
  const codeReaderRef = useRef(createCodeReader());
  const scanIntervalRef = useRef(null);
  const cropCanvasRef = useRef(null);
  const isClosingRef = useRef(false);
  const isDecodingRef = useRef(false);
  const candidateHitsRef = useRef(new Map());

  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState("");
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [confirmingCode, setConfirmingCode] = useState("");

  const resetStableScan = useCallback(() => {
    candidateHitsRef.current = new Map();
    setConfirmingCode("");
  }, []);

  const clearScanInterval = () => {
    if (scanIntervalRef.current) {
      window.clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
  };

  const stopScanner = useCallback(() => {
    clearScanInterval();
    isDecodingRef.current = false;
    codeReaderRef.current?.reset();
    setIsCameraActive(false);
    resetStableScan();
  }, [resetStableScan]);

  const handleCloseModal = useCallback(() => {
    isClosingRef.current = true;
    stopScanner();
    setError("");
    setShowModal(false);
    window.setTimeout(() => {
      isClosingRef.current = false;
    }, 0);
  }, [stopScanner]);

  const handleScanSuccess = useCallback(
    text => {
      const normalizedText = normalizeScannedText(text);

      if (!normalizedText || isClosingRef.current) {
        return;
      }

      clearScanInterval();
      codeReaderRef.current?.reset();
      handleResult(normalizedText);
      handleCloseModal();
    },
    [handleResult, handleCloseModal]
  );

  const recordCandidate = useCallback(
    text => {
      if (isClosingRef.current) {
        return;
      }

      const normalizedText = normalizeScannedText(text);

      if (!normalizedText) {
        return;
      }

      const hits = candidateHitsRef.current;
      hits.set(normalizedText, (hits.get(normalizedText) || 0) + 1);

      const leadingCode = pickLeadingCandidate(hits);

      if (leadingCode) {
        handleScanSuccess(leadingCode);
        return;
      }

      let previewCode = "";
      let previewCount = 0;

      hits.forEach((count, code) => {
        if (count > previewCount) {
          previewCount = count;
          previewCode = code;
        }
      });

      setConfirmingCode(previewCount >= 1 ? previewCode : "");
    },
    [handleScanSuccess]
  );

  const captureFrameScreenshot = useCallback(() => {
    const video = webcamRef.current?.video;

    if (!video?.videoWidth || !video?.videoHeight) {
      return null;
    }

    const videoWidth = video.videoWidth;
    const videoHeight = video.videoHeight;
    const cropWidth = videoWidth * 0.72;
    const cropHeight = cropWidth / 1.6;
    const cropX = (videoWidth - cropWidth) / 2;
    const cropY = (videoHeight - cropHeight) / 2;

    const scale =
      cropWidth > MAX_DECODE_WIDTH ? MAX_DECODE_WIDTH / cropWidth : 1;
    const outputWidth = Math.round(cropWidth * scale);
    const outputHeight = Math.round(cropHeight * scale);

    if (!cropCanvasRef.current) {
      cropCanvasRef.current = document.createElement("canvas");
    }

    const canvas = cropCanvasRef.current;
    canvas.width = outputWidth;
    canvas.height = outputHeight;

    const context = canvas.getContext("2d");

    if (!context) {
      return webcamRef.current?.getScreenshot();
    }

    context.drawImage(
      video,
      cropX,
      cropY,
      cropWidth,
      cropHeight,
      0,
      0,
      outputWidth,
      outputHeight
    );

    return canvas.toDataURL("image/png");
  }, []);

  const decodeScreenshot = useCallback(
    imageSrc => {
      if (isDecodingRef.current || isClosingRef.current) {
        return;
      }

      isDecodingRef.current = true;

      codeReaderRef.current
        .decodeFromImage(undefined, imageSrc)
        .then(result => {
          if (result) {
            recordCandidate(result.getText());
          }
        })
        .catch(() => {})
        .finally(() => {
          isDecodingRef.current = false;
        });
    },
    [recordCandidate]
  );

  const startScanLoop = useCallback(() => {
    clearScanInterval();

    scanIntervalRef.current = window.setInterval(() => {
      const imageSrc = captureFrameScreenshot();

      if (imageSrc) {
        decodeScreenshot(imageSrc);
      }
    }, SCAN_TICK_MS);
  }, [captureFrameScreenshot, decodeScreenshot]);

  const handleUserMedia = () => {
    setError("");
    setIsCameraActive(true);
    startScanLoop();
  };

  const handleUserMediaError = mediaError => {
    stopScanner();

    const errorName = String(mediaError?.name ?? "");

    if (errorName === "NotAllowedError") {
      setError(
        "Bị chặn quyền camera. Bấm ổ khóa trên thanh địa chỉ → Camera → Cho phép, rồi tải lại trang."
      );
      return;
    }

    if (errorName === "NotFoundError") {
      setError("Không tìm thấy camera trên thiết bị.");
      return;
    }

    setError("Không thể mở camera. Vui lòng cho phép quyền camera và thử lại.");
  };

  const handleOpenModal = () => {
    setError("");
    setIsCameraActive(false);
    resetStableScan();
    codeReaderRef.current = createCodeReader();
    setShowModal(true);
  };

  useEffect(() => {
    return () => {
      clearScanInterval();
      codeReaderRef.current?.reset();
      cropCanvasRef.current = null;
    };
  }, []);

  return (
    <>
      <button
        type="button"
        className="ticket-barcode-scanner-trigger"
        onClick={handleOpenModal}
        title="Quét Barcode"
        aria-label="Quét Barcode"
      >
        <i className="bx bx-barcode text-info" />
      </button>
      <Modal
        isOpen={showModal}
        toggle={handleCloseModal}
        centered
        fade={false}
        zIndex={TICKET_SCANNER_MODAL_Z_INDEX}
        backdropClassName="ticket-barcode-scanner-backdrop"
        modalClassName="ticket-barcode-scanner-modal"
        onClosed={stopScanner}
      >
        <ModalHeader toggle={handleCloseModal}>Quét mã vạch / QR</ModalHeader>
        <ModalBody className="p-0">
          <div className="ticket-barcode-scanner-webcam-wrap">
            {showModal ? (
              <Webcam
                ref={webcamRef}
                audio={false}
                mirrored={false}
                forceScreenshotSourceSize
                screenshotFormat="image/png"
                videoConstraints={VIDEO_CONSTRAINTS}
                onUserMedia={handleUserMedia}
                onUserMediaError={handleUserMediaError}
                className="ticket-barcode-scanner-video"
              />
            ) : null}
            <div className="ticket-barcode-scanner-overlay">
              <div className="ticket-barcode-scanner-frame" />
            </div>
            {!isCameraActive && !error ? (
              <div className="ticket-barcode-scanner-status">Đang mở camera...</div>
            ) : null}
            {isCameraActive ? (
              <div className="ticket-barcode-scanner-hint">
                {confirmingCode
                  ? `Đang nhận: ${confirmingCode}`
                  : "Đặt mã vào khung giữa, giữ ổn định"}
              </div>
            ) : null}
            {error ? (
              <div className="ticket-barcode-scanner-error">{error}</div>
            ) : null}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={handleCloseModal}>
            Đóng
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default TicketBarcodeScannerButton;
